import { capitalize } from 'lodash';
import axios from 'axios';
import {
  FileDetails,
  IApplicationPreviewComments,
  LogDisplayItem,
  LogEntry,
  OptionI
} from '../../types/interface';
import { errorNotification } from 'base/notifications/Notifications';

import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getCookie } from './cookieUtils';
import { GERMAN, SELECTED_LANGUAGE } from './enums/misc';

const lang = getCookie(SELECTED_LANGUAGE);

export const isProd = process.env?.REACT_APP_IS_PROD === 'true';
export const isDev = !isProd;

export const executeInDev = (fn: () => void) => {
  if (isDev) {
    fn();
  }
};

export const keyboardUtilityCodes = () => {
  const allowedCodes: number[] = [];

  //allows in the sequeence arrow keys, backspace, tab, enter
  allowedCodes.push(37, 39, 8, 9, 13);
  return allowedCodes;
};

export function convertToLabelValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arr: any[],
  labelKey: string,
  valueKey: string
) {
  return arr.map(item => {
    const formattedItem: OptionI = {
      value: '',
      label: ''
    };
    if (typeof item === 'string') {
      formattedItem.label = capitalizeFirstLetter(item);
      formattedItem.value = item;
    } else {
      formattedItem.label = capitalizeFirstLetter(item[labelKey]);
      formattedItem.value = item[valueKey];
    }

    return formattedItem;
  });
}

export const getLastUrl = (url: string, sections: number) => {
  let urlComponents = url.split('/');

  urlComponents = urlComponents.slice(sections * -1);

  return urlComponents.join('/');
};

// download document.
async function getFileAsBlob(url: string): Promise<string | undefined> {
  try {
    const response = await axios.get(url, {
      responseType: 'blob'
    });

    if (response?.status === 200) {
      const file = new Blob([response.data], { type: response.data.type });
      const objectUrl = URL.createObjectURL(file);
      return objectUrl;
    } else {
      errorNotification(response?.data?.message);
      return undefined;
    }
  } catch (e) {
    return undefined;
  }
}

function downloadFileFromBlob(url: string, name: string) {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = name || '';
  document.body.appendChild(a);
  a.click();
}

async function downloadFileAsBlob(
  url: string,
  name: string,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  const blobUrl = await getFileAsBlob(url);
  if (blobUrl) {
    downloadFileFromBlob(blobUrl, name);
  }
  window.URL.revokeObjectURL(blobUrl!);
  setIsLoading(false);
}

export const downloadFile = (
  filePath: string,
  fileName: string,
  setDownloadLoader: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setDownloadLoader(true);
  downloadFileAsBlob(filePath, fileName, setDownloadLoader);
};

export const convertToCurrency = (number: number | string) => {
  if (number === null || number === undefined || isNaN(number as number)) {
    return '€0.00';
  }

  let input: number;

  if (typeof number === 'string') {
    const cleanedInput = number.replace(/[€$\s]/g, '');

    const normalizedInput = cleanedInput.replace(/,/g, '');

    input = parseFloat(normalizedInput);
  } else {
    input = number;
  }

  const isNegative = input < 0;
  const absoluteNumber = Math.abs(input);

  const euros = Math.floor(absoluteNumber);
  const cents = Math.round((absoluteNumber - euros) * 100);

  const eurosStr = euros.toString();
  const eurosWithSeparator = eurosStr
    .split('')
    .reverse()
    .reduce((acc, digit, i) => {
      if (i > 0 && i % 3 === 0) {
        return digit + '.' + acc;
      }
      return digit + acc;
    }, '');

  const formattedNumber = `${
    isNegative ? '-' : ''
  }€${eurosWithSeparator},${cents.toString().padStart(2, '0')}`;

  return formattedNumber;
};

export const formatAndCheckIban = (iban: string) => {
  let cleaned = iban.replace(/[^\w]/g, '');

  cleaned = cleaned.replace(/^([^A-Za-z]*)(.*)/g, '$2');
  cleaned =
    cleaned.slice(0, 2).replace(/[^A-Za-z]/g, '') +
    cleaned.slice(2).replace(/[^0-9]/g, '');

  cleaned = cleaned.slice(0, 20);

  if (cleaned.length >= 2) {
    cleaned = cleaned.slice(0, 2).toUpperCase() + cleaned.slice(2);
  }

  const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();

  return formatted;
};

export function convertDateFormat(
  dateString: string | null | undefined | Date
): string {
  if (!dateString) return '--';
  const date: Date = new Date(dateString);
  const parts = [date.getDate(), date.getMonth() + 1, date.getFullYear()];

  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export const formatDateOnly = (date: Date | string | null) => {
  if (!date) return '';
  const inputDate = new Date(date);
  const year = inputDate.getFullYear();
  const month = String(inputDate.getMonth() + 1).padStart(2, '0');
  const day = String(inputDate.getDate()).padStart(2, '0');
  return `${day}.${month}.${year}`;
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return '';
  if (lang === GERMAN) {
    return string;
  } else {
    return capitalize(string);
  }
};

export const formatAndConvertSubstring = (text: string) => {
  if (!text) return '';
  if (text?.length <= 38) {
    return capitalizeFirstLetter(text?.replace(/[_-]/g, ' '));
  } else {
    return (
      capitalizeFirstLetter(text?.substring(0, 20)?.replace(/[_-]/g, ' ')) +
      '...'
    );
  }
};

export const formatString = (text: string | undefined) => {
  if (!text) return '';
  return capitalizeFirstLetter(text?.replace(/[_-]/g, ' '));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function showValue(value: any) {
  if (typeof value === 'boolean') {
    if (value === true) {
      return t('delete_model.button.text_1');
    } else {
      return t('delete_model.button.text_3');
    }
  }
  if (
    value === '' ||
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return '--';
  }
  return formatAndConvertSubstring(value);
}

export function checkAccess(
  permissionsNeeded: string[],
  userPermission: string[]
): boolean {
  if (!userPermission.length) {
    return false;
  }

  if (!permissionsNeeded.length) {
    return false;
  }

  for (const permission of permissionsNeeded) {
    if (userPermission.includes(permission)) {
      return true;
    }
  }

  return false;
}

export const convertFromCurrency = (
  formattedCurrency: string
): number | null => {
  const cleanString = formattedCurrency
    .replace('€', '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();

  if (parseFloat(cleanString)) {
    return parseFloat(cleanString);
  } else {
    return null;
  }
};

export const parseIntoNumber = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (Array.isArray(value) || typeof value === 'object') return 0;

  const cleaned = typeof value === 'string' ? value.replace(/,/g, '') : value;
  const num = Number(cleaned);

  if (isNaN(num) || !isFinite(num)) return 0;
  return num;
};

export function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
}

export const checkValidValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value) && isFinite(value);
  }

  return false;
};

export const extractPdfUrls = (obj: any): string[] => {
  const urls: string[] = [];

  const extract = (value: any) => {
    if (Array.isArray(value)) {
      value.forEach(extract);
    } else if (typeof value === 'object' && value !== null) {
      if (
        value.file_path &&
        typeof value.file_path === 'string'
        // value.file_path.endsWith('.pdf')
      ) {
        urls.push(value.file_path);
      }
      Object.values(value).forEach(extract);
    }
  };

  console.log(obj);
  console.log(urls);

  extract(obj);
  return urls;
};

import { commonDownloadFile } from '../redux/common/commonAction';
import { Timeline, TimelineItem } from 'data/timelineData';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faClock } from '@fortawesome/free-solid-svg-icons';

export const downloadFileFromUrl = async ({
  fileData,
  setDownloadLoader
}: {
  fileData: FileDetails;
  setDownloadLoader: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  try {
    setDownloadLoader(true);
    const response = await commonDownloadFile(fileData.file_name);
    if (response.status === 201) {
      const blob = new Blob([response.data], { type: fileData.mimetype });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileData.file_name || 'download.pdf');
      document.body.appendChild(link);
      link.click();
      setDownloadLoader(false);
    } else {
      errorNotification(response.data.message);
      setDownloadLoader(false);
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    setDownloadLoader(false);
  }
};

export function convertLogsToTimeline(logs: LogEntry[]): Timeline[] {
  const grouped: Record<string, TimelineItem[]> = {};

  logs.forEach((log, index) => {
    const date = log.time.split('T')[0]; // yyyy-mm-dd

    const item: TimelineItem = {
      indexed_id: index + 1,
      id: log.log_id,
      time: formatDateCET(log.time),
      icon: faClock as IconProp, // ✅ same icon for all
      iconColor: 'danger', // ✅ same color for all
      title: capitalizeWords(log.type.replaceAll(/[_-]/g, ' ')),
      content: formateLogDate(log.message),
      tasker: `${capitalizeWords(log.user.first_name)} ${capitalizeWords(
        log.user.last_name
      )}`
    };

    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(item);
  });

  return Object.entries(grouped).map(([date, items], index) => ({
    id: index + 1,
    date: formatDateOnly(date),
    items
  }));
}

function capitalizeWords(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatUTCDateWithTime(utcDate: string | Date): string {
  const date = new Date(utcDate);

  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${formatDateOnly(date)} ${hours}:${minutes}:${seconds}`;
}

export function isFieldEmptyAndInOverview(
  inOverview: boolean,
  hasValue: boolean
) {
  if (inOverview && !hasValue) {
    return 'd-none';
  } else {
    return '';
  }
}

export function formatDateCET(date: Date | string | null): string {
  if (!date) return '';

  const inputDate = new Date(date);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const options: Intl.DateTimeFormatOptions = {
    timeZone: userTimeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };

  const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(
    inputDate
  );

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';

  return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get(
    'minute'
  )}:${get('second')}`;
}

export function formateLogDate(v: string): string {
  const fullDateTime = v.match(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[+-]\d{2}:\d{2})?/
  );

  if (fullDateTime) {
    const onlyDate = formatDateCET(fullDateTime![0]);

    // Replace datetime with date
    return v.replace(fullDateTime![0], onlyDate);
  } else {
    return v;
  }
}

export function logEntriesToDisplayItems(
  entries: LogEntry[],
  isGerman: boolean
): LogDisplayItem[] {
  return entries.map(entry => ({
    id: entry.log_id,
    type: entry.type,
    message: formateLogDate(
      isGerman && entry.message_de ? entry.message_de : entry.message
    ),
    timestamp: formatDateCET(entry.time),
    date: entry.time?.split('T')[0] ?? entry.created_at?.split('T')[0] ?? '',
    userName: `${capitalizeWords(
      entry.user?.first_name ?? ''
    )} ${capitalizeWords(entry.user?.last_name ?? '')}`.trim()
  }));
}

export function applicationCommentsToDisplayItems(
  comments: IApplicationPreviewComments[]
): LogDisplayItem[] {
  return comments.map((c, i) => ({
    id: i,
    type: c.type,
    message: c.message,
    comment: c.comment || undefined,
    timestamp: formatDateCET(c.created_at),
    date: (() => {
      const raw = c.created_at?.split('T')[0] ?? '';
      const parts = raw.split('-');
      return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : raw;
    })()
  }));
}
